import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { RecommendationCommunityFilterType, RecommendationCommunityOverviewType } from 'types/recommendations';

import RecommendationCommunityStructured from './RecommendationCommunityStructured';
import RecommendationCommunityWidgets from './RecommendationCommunityWidgets';
import useRecommendationDetailsContext from './RecommendationDetailsContext';
import RecommendationMarkdown from './RecommendationMarkdown';
import { parseStructureFromJSON } from '../../utils/helpers/object';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 60px;
  line-height: 1.5;
`;

const Header = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;

  a {
    font-size: 22px;
    font-family: var(--font-bold);
    color: var(--text-primary);
    text-decoration: underline;
  }

  a:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ReasoningContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const ReasoningTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);

  span:first-child {
    font-size: 14px;
    font-family: var(--font-bold);
    color: var(--text-primary);
  }

  span:not(:first-child):before {
    content: '\\00B7';
    padding-right: 5px;
    padding-left: 5px;
  }
`;

const ReasoningItems = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ContentRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-direction: column;
`;

const NoData = styled.div`
  width: 100%;
  height: 100%;
  color: var(--text-secondary);
  font-family: var(--font-bold);
  font-size: 16px;
  text-align: center;
  align-content: center;
`;

type ReasoningVariantType = 'summary' | 'details';

type ReasoningType = {
  tags: string[];
  description: string;
};

interface RecommendationCommunityProps {
  data: RecommendationCommunityOverviewType;
}

const RecommendationCommunity = ({ data }: RecommendationCommunityProps) => {
  const { widgets, filters: globalFilters } = useRecommendationDetailsContext();
  const availableWidgets = widgets ?? [];
  const [widget, setWidget] = useState(availableWidgets.length > 0 ? availableWidgets[0] : 0);
  const [filters, setFilters] = useState<RecommendationCommunityFilterType>({
    careTypes: globalFilters?.careTypes || [],
    unitTypes: [],
    careLevels: [],
    reviewSources: [],
    reviewCategories: [],
    swotCategories: [],
    competitors: [],
  });
  const [reasoningVariant, setReasoningVariant] = useState<ReasoningVariantType | undefined>();
  const [reasoning, setReasoning] = useState<ReasoningType[]>([]);

  const hasWidgets = availableWidgets.length > 0;

  useEffect(() => {
    if (filters.careTypes.length === 0 && filters.unitTypes.length === 0) {
      setReasoningVariant('summary');
    } else {
      setReasoningVariant('details');
      setReasoning(
        data.recommendations.reduce((acc, r) => {
          const hasCareType = filters.careTypes.length > 0 ? filters.careTypes.includes(r.care_type_id) : true;
          const hasUnitType = filters.unitTypes.length > 0 ? filters.unitTypes.includes(r.unit_type_id) : true;

          if (hasCareType && hasUnitType) {
            acc.push({
              tags: [r.unit_type_name, r.care_type_name].filter(Boolean),
              description: `${r.problem} ${r.solution}`,
            });
          }

          return acc;
        }, [] as any[])
      );
    }
  }, [filters]);

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFilters((draft) => ({ ...draft, ...newFilters }));
  };

  const handleWidgetChange = (widget: number) => {
    setWidget(widget);
  };

  if (!reasoningVariant) {
    return null;
  }

  const structuredSolution = parseStructureFromJSON(data.solution);

  return (
    <Container>
      <ContentLeft>
        <Header>
          <Link to={`/community/${data.community_id}`} target="_blank" rel="noopener noreferrer">
            {data.community_name}
          </Link>
        </Header>
        {reasoningVariant === 'summary' && (
          <ReasoningContainer>
            <ReasoningItems>
              <RecommendationMarkdown>{data.problem}</RecommendationMarkdown>
              {structuredSolution ? (
                <RecommendationCommunityStructured
                  structure={structuredSolution}
                  communityId={data.community_id}
                  communityName={data.community_name}
                />
              ) : (
                <RecommendationMarkdown>{data.solution}</RecommendationMarkdown>
              )}
            </ReasoningItems>
          </ReasoningContainer>
        )}
        {reasoningVariant === 'details' &&
          reasoning.map((r, i) => (
            <ReasoningContainer key={i}>
              <ReasoningItems>
                <ReasoningTitle>
                  {r.tags.map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </ReasoningTitle>
                <RecommendationMarkdown>{r.description}</RecommendationMarkdown>
              </ReasoningItems>
            </ReasoningContainer>
          ))}
        {reasoningVariant === 'details' && reasoning.length === 0 && (
          <NoData>No recommendations for selected filters</NoData>
        )}
      </ContentLeft>
      {hasWidgets && (
        <ContentRight>
          <RecommendationCommunityWidgets
            communityId={data.community_id}
            data={data}
            widget={widget}
            availableWidgets={availableWidgets}
            filters={filters}
            onWidgetChange={handleWidgetChange}
            onFilterChange={handleFiltersChange}
          />
        </ContentRight>
      )}
    </Container>
  );
};

export default RecommendationCommunity;
